import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { Box, Button } from '@mui/material';
import { TabContext } from '@mui/lab';
import { CustomTab, CustomTabs } from 'ui/tabs/tabs';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteIcon from '@mui/icons-material/Delete';

import { BorderLinearProgress } from 'components/Loader/Loader';

import { Button as SpectrumButton } from '@bestdoctor/spectrum-uikit/Button';

import { boxNavStyle } from 'ui/navigation/navigation';
import { mainBtnStyle } from 'ui/buttons/buttonsMain';
import { grayDarkColor } from 'utils/uiConstants';

import { useGetCompanies } from 'hooks/companies/useGetCompanies';
import { Company, ErrorData } from 'interfaces/global/globalInterfaces';

import { NotificationFilters } from 'components/NotificationsList/NotificationFilters';
import { NotificationTable } from 'components/NotificationsList/NotificationTable';
import {
  Notification,
  NotificationFilterByPeriod,
  ParamsPriorityFilter,
} from 'interfaces/notifications/notificationsInterfaces';
import { useNotifications } from 'hooks/notifications/useNotifications';
import { throttle } from 'utils/tablesMethods';
import { useViewedAllNotifications } from 'hooks/notifications/useViewedAllNotifications';
import { useRemoveNotifications } from 'hooks/notifications/useRemoveNotifications';
import { useSnackbar } from 'notistack';

export const Notifications: React.FC = () => {
  const queryClient = useQueryClient();

  const [notificationsData, setNotificationsData] = useState<Notification[] | null>(null);
  const [menuValue, setMenuValue] = useState<'' | 'payments' | 'status_change'>('');

  // check уведомлений
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedForViewed, setSelectedForViewed] = useState<string[]>([]);

  // стейты главных фильтров
  const [selectedCompaniesInNotifications, setSelectedCompaniesInNotifications] = useState<Company | null>(null);
  const [notificationFilterByPeriod, setNotificationFilterByPeriod] = useState<NotificationFilterByPeriod | null>(null);
  const [priority, setPriority] = useState<ParamsPriorityFilter | null>(null);
  const [unviewed, setUnviewed] = useState<boolean>(false);
  const [orderingTableNotifications, setOrderingTableNotifications] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const { isLoadingCompanies, companiesSearch: allCompanies } = useGetCompanies();

  const {
    isLoadingNotifications,
    fetchNextPageNotifications,
    hasNextPageNotifications,
    isFetchingNextPageNotifications,
  } = useNotifications({
    statusNotifications: menuValue,
    selectedCompanies: selectedCompaniesInNotifications,
    period: notificationFilterByPeriod,
    priority: priority?.value || '',
    status: unviewed,
    ordering: orderingTableNotifications,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
    onSuccess: (data: { slotsNotification: Notification[]; pageParams: unknown[] }) =>
      setNotificationsData(data.slotsNotification),
  });

  const { handleViewedAllNotifications } = useViewedAllNotifications({
    notificationUuids: selectedForViewed,
    onSuccess: () => {
      setSelected([]);
      setSelectedForViewed([]);
      queryClient.invalidateQueries('dataUnviewedNotifications');
    },
  });

  const { handleRemoveNotifications } = useRemoveNotifications({
    onSuccess: () => {
      setSelected([]);
      setSelectedForViewed([]);
      queryClient.invalidateQueries('dataUnviewedNotifications');
    },
  });

  const handleScroll = async (evt: Event): Promise<void> => {
    const { scrollHeight, scrollTop } = (evt.target as Document).documentElement;
    const windowHeight = window.innerHeight;

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    if (scrollHeight - (windowHeight + scrollTop) < 50) {
      await fetchNextPageNotifications();
    }
  };

  useEffect(() => {
    if (hasNextPageNotifications && !isFetchingNextPageNotifications) {
      document.addEventListener('scroll', throttle(handleScroll, 1000));
    }

    return () => document.removeEventListener('scroll', throttle(handleScroll, 1000));
  }, [hasNextPageNotifications, isFetchingNextPageNotifications]);

  return (
    <>
      {(isLoadingCompanies || isLoadingNotifications) && <BorderLinearProgress />}
      <TabContext value={menuValue}>
        <Box component="section" sx={{ ...boxNavStyle, gap: '17px' }}>
          <Link to="/staff" style={{ textDecoration: 'none' }}>
            <Button
              sx={{ ...mainBtnStyle(), color: grayDarkColor }}
              variant="text"
              startIcon={<KeyboardBackspaceIcon sx={{ width: '16px' }} />}
            >
              Назад
            </Button>
          </Link>
          <CustomTabs
            sx={{ marginRight: 'auto' }}
            value={menuValue}
            onChange={(_event: React.SyntheticEvent, newValue: '' | 'payments' | 'status_change'): void =>
              setMenuValue(newValue)
            }
            aria-label="Табы"
          >
            <CustomTab value="" label="Все уведомления" />
            <CustomTab value="slot_action" label="Операции с пациентами" />
            <CustomTab value="payments" label="Платежи" />

            <CustomTab value="status_change" label="Смена статуса ДС" />
          </CustomTabs>

          <SpectrumButton
            variant="secondary"
            leftElement={<DoneAllIcon sx={{ width: '16px' }} />}
            onClick={handleViewedAllNotifications}
            disabled={selectedForViewed.length === 0}
          >
            Прочитать выбранные
          </SpectrumButton>
          <SpectrumButton
            variant="secondary"
            leftElement={<DeleteIcon sx={{ width: '16px' }} />}
            onClick={() => handleRemoveNotifications(selected)}
            disabled={selectedForViewed.length === 0}
          >
            Удалить выбранные
          </SpectrumButton>
        </Box>
        <NotificationFilters
          allCompanies={allCompanies}
          selectedCompaniesInNotifications={selectedCompaniesInNotifications}
          notificationFilterByPeriod={notificationFilterByPeriod}
          priority={priority}
          unviewed={unviewed}
          setSelectedCompaniesInNotifications={setSelectedCompaniesInNotifications}
          setNotificationFilterByPeriod={setNotificationFilterByPeriod}
          setPriority={setPriority}
          setUnviewed={setUnviewed}
        />

        {notificationsData && !!notificationsData?.length && (
          <NotificationTable
            notificationsData={notificationsData}
            isFetchingNextPageNotifications={isFetchingNextPageNotifications}
            orderingTableNotifications={orderingTableNotifications}
            selected={selected}
            selectedForViewed={selectedForViewed}
            setOrderingTableNotifications={setOrderingTableNotifications}
            setSelected={setSelected}
            setSelectedForViewed={setSelectedForViewed}
          />
        )}
      </TabContext>
    </>
  );
};
